import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api';
import { track, identifyUser } from '../utils/analytics';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('rb_token');
    if (token) {
      api.getMe()
        .then(({ user }) => setUser(user))
        .catch((err) => {
          // Only clear token for auth failures, not network errors
          if (err.message && (err.message.includes('401') || err.message.toLowerCase().includes('unauthorized'))) {
            localStorage.removeItem('rb_token');
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleForceLogout = () => {
      setUser(null);
    };
    window.addEventListener('auth:logout', handleForceLogout);
    return () => window.removeEventListener('auth:logout', handleForceLogout);
  }, []);

  const setAuth = ({ token, user }) => {
    localStorage.setItem('rb_token', token);
    setUser(user);
    identifyUser(user?._id || user?.id);
  };

  const requestSignupCode = async (name, email, password) => {
    return api.requestSignupCode(name, email, password);
  };

  const getPendingReferralCode = () => localStorage.getItem('mars_referral_code') || '';
  const clearPendingReferralCode = () => localStorage.removeItem('mars_referral_code');

  const signup = async (name, email, password, code, referralCode) => {
    const ref = referralCode || getPendingReferralCode();
    const data = await api.signup(name, email, password, code, ref || undefined);
    setAuth(data);
    track('sign_up', { method: 'email' });
    if (data.isNewUser) { localStorage.setItem('mars_onboarding_pending', 'true'); clearPendingReferralCode(); }
    return data;
  };

  const requestLoginCode = async (email, password) => {
    return api.requestLoginCode(email, password);
  };

  const verifyLogin = async (email, code) => {
    const data = await api.verifyLogin(email, code);
    setAuth(data);
    track('login', { method: 'email' });
    return data;
  };

  const login = async (email, password) => {
    const data = await api.login(email, password);
    setAuth(data);
    track('login', { method: 'email' });
    return data;
  };

  const googleLogin = async (credential, referralCode) => {
    const ref = referralCode || getPendingReferralCode();
    const data = await api.googleAuth(credential, ref || undefined);
    setAuth(data);
    track(data.isNewUser ? 'sign_up' : 'login', { method: 'google' });
    if (data.isNewUser) { localStorage.setItem('mars_onboarding_pending', 'true'); clearPendingReferralCode(); }
    return data;
  };

  const linkedinLogin = async (code, redirectUri, referralCode) => {
    const ref = referralCode || getPendingReferralCode();
    const data = await api.linkedinAuth(code, redirectUri, ref || undefined);
    setAuth(data);
    track(data.isNewUser ? 'sign_up' : 'login', { method: 'linkedin' });
    if (data.isNewUser) { localStorage.setItem('mars_onboarding_pending', 'true'); clearPendingReferralCode(); }
    return data;
  };

  const logout = () => {
    localStorage.removeItem('rb_token');
    setUser(null);
  };

  const updateProfile = async (profile) => {
    const { user: updated } = await api.updateProfile(profile);
    setUser(updated);
    return updated;
  };

  const refreshUser = async () => {
    const { user: fresh } = await api.getMe();
    setUser(fresh);
    return fresh;
  };

  return (
    <AuthContext.Provider value={{ user, loading, requestSignupCode, signup, login, requestLoginCode, verifyLogin, googleLogin, linkedinLogin, logout, updateProfile, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
